import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CompanyLegalDetails } from '../components/CompanyLegalDetails';
import { ETAPPIO_COMPANY } from '../constants/company';
import { systemApi, type DeliveryInformationDocument } from '../services/api';

const formatSavedDate = (value: string) =>
  new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date(value));

export function DeliveryInformation() {
  const [document, setDocument] = useState<DeliveryInformationDocument | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    window.document.title = 'Leveranser och ansvar på Etappio';
    systemApi.getDeliveryInformation()
      .then(setDocument)
      .catch(() => setFailed(true));
  }, []);

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <header className="bg-white border-bottom py-3">
        <div className="container d-flex align-items-center justify-content-between">
          <Link to="/" aria-label="Etappio startsida">
            <img src="/assets/images/logo-full.png" alt="ETAPPIO" height="38" />
          </Link>
          <Link to="/" className="btn btn-light-brand btn-sm">
            Till startsidan
          </Link>
        </div>
      </header>

      <main className="container py-5 flex-grow-1" style={{ maxWidth: '920px' }}>
        <section
          className="rounded-4 p-4 p-md-5 mb-4 text-white shadow-sm"
          style={{ background: 'linear-gradient(135deg, #173b67 0%, #095bb7 58%, #2f8be6 100%)' }}
        >
          <span className="badge rounded-pill bg-white text-primary mb-3 px-3 py-2">
            Rätt ansvar på rätt plats
          </span>
          <h1 className="fw-bold mb-3 text-white">Leveranser och ansvar på Etappio</h1>
          <p className="mb-0 fs-5" style={{ maxWidth: '760px', lineHeight: 1.55 }}>
            Etappio håller ihop projekt, milstolpar, dokumentation och betalningar. Entreprenören
            ansvarar för själva arbetet. Här ser du vem du ska vända dig till och vad som behöver
            vara på plats för att projektet ska flyta smidigt.
          </p>
        </section>

        <article className="card border-0 shadow-sm">
          <div className="card-body p-4 p-md-5">
            {!document && !failed && (
              <div className="d-flex align-items-center gap-2 text-muted py-5" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                Hämtar aktuell leveransinformation…
              </div>
            )}

            {failed && (
              <div className="alert alert-danger" role="alert">
                Aktuell information om leveranser och ansvar kan inte visas just nu. Försök
                igen senare eller kontakta{' '}
                <a href={`mailto:${ETAPPIO_COMPANY.email}`}>{ETAPPIO_COMPANY.email}</a>.
              </div>
            )}

            {document && (
              <>
                <p className="text-muted border-bottom pb-3 mb-4" data-testid="delivery-version">
                  Version {document.version} · Senast uppdaterad{' '}
                  <time dateTime={document.savedAt}>
                    {formatSavedDate(document.savedAt)}
                  </time>
                </p>
                <div
                  className="ql-editor p-0"
                  data-testid="delivery-content"
                  dangerouslySetInnerHTML={{ __html: document.content }}
                />
              </>
            )}
          </div>
        </article>
      </main>

      <footer className="bg-dark text-white py-4">
        <div className="container text-center">
          <p className="small text-white-50 mb-2">
            © 2026 {ETAPPIO_COMPANY.legalName} ·{' '}
            <Link className="text-white-50" to="/anvandarvillkor">Användarvillkor</Link>
          </p>
          <CompanyLegalDetails variant="compact" theme="dark" />
        </div>
      </footer>
    </div>
  );
}
